import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { DashboardContent } from '@/layouts/dashboard';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow, TableUser, TableUserContent, TableUserTitle } from '@/components/ui/table';
import { AvatarWrapper } from '@/components/ui/avatar';
import { liveChallengesApi } from '@/services/liveChallenges';
import { PATHS } from '@/routes/paths';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { TiaBanner } from '@/components/custom/tia-banner';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { DESAFIO_AO_VIVO } from '@/pages/dashboard/home-teacher/teacher-character';
import { useSession } from '@/hooks/useSession';
import { useAuthorityLevels } from '@/hooks/useAuthorityLevel';

const STATUS_CONFIG = {
  lobby: { label: 'Lobby', variant: 'warning' },
  in_progress: { label: 'Em Andamento', variant: 'primary-light' },
  finished: { label: 'Finalizada', variant: 'success-light' }
};

// Badges "soft" só para a tabela do tema playful — a lista default usa
// STATUS_CONFIG acima (fundo cheio).
const PLAYFUL_STATUS_BADGE = {
  lobby: { label: 'Lobby', variant: 'amber-soft' },
  in_progress: { label: 'Em Andamento', variant: 'info-soft' },
  finished: { label: 'Finalizada', variant: 'success-soft' }
};

const ACTIVATION_TABLE_HEAD = [
  { label: 'Data da ativação', width: 170 },
  { label: 'Host', width: 160 },
  { label: 'Participantes', width: 130 },
  { label: 'Código', width: 110 },
  { label: 'Status', width: 130 },
  { label: '', width: 140 }
];

const normalizeJsonApi = items =>
  items.map(item => ({
    id: item.id,
    ...Object.fromEntries(Object.entries(item.attributes).map(([k, v]) => [k.replace(/-/g, '_'), v]))
  }));

export default function ActivationsList() {
  const { id } = useParams();
  const navigate = useNavigate();
  const playfulTeacher = usePlayfulTeacher();
  const { session } = useSession();
  const authorityLevels = useAuthorityLevels();
  const isAdmin = authorityLevels.includes('admin');
  const [activations, setActivations] = useState([]);
  const [challengeTitle, setChallengeTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivations = async () => {
      setLoading(true);
      // Fetches paralelos com falha independente: título indisponível não pode
      // derrubar a lista nem disparar toast enganoso (e vice-versa).
      const [activationsResult, challengeResult] = await Promise.allSettled([
        liveChallengesApi.activations(id),
        // RECONCILIAÇÃO backend: index de ativações não envia meta — título vem do show do template.
        liveChallengesApi.show(id)
      ]);
      if (activationsResult.status === 'fulfilled') {
        setActivations(normalizeJsonApi(activationsResult.value?.data ?? []));
      } else {
        toast.error('Erro ao carregar ativações.');
      }
      if (challengeResult.status === 'fulfilled') {
        setChallengeTitle(challengeResult.value?.data?.attributes?.title ?? '');
      }
      setLoading(false);
    };
    fetchActivations();
  }, [id]);

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      { route: PATHS.LIVE_CHALLENGE.ADMIN_LIST, name: 'Desafio ao Vivo' },
      { route: '#', name: challengeTitle ? `Ativações — ${challengeTitle}` : 'Ativações', isActive: true }
    ]
  };

  const formatDateTime = value => (value ? new Date(value).toLocaleString('pt-BR') : '—');

  // Professor só abre o painel da própria ativação — admin abre qualquer uma.
  const canOpenPanel = activation => isAdmin || String(activation.host?.id) === String(session.user.id);

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-24">
          <TiaBanner
            className="h-full"
            image={DESAFIO_AO_VIVO}
            imageAlt="Personagem do professor"
            imageHeight={215}
            imageOffset={250}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text="Suas ativações ao vivo! 🏆 Acompanhe quem já jogou, quantos participantes entraram e reabra o painel quando quiser."
          />
        </div>
      )}

      {loading ? (
        <Loader />
      ) : activations.length === 0 ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <EmptyState
            type="data"
            text="Este desafio ainda não foi ativado."
          />
        </div>
      ) : playfulTeacher ? (
        <Table>
          <TableHeaderWrapper tableHead={ACTIVATION_TABLE_HEAD} />
          <TableBody>
            {activations.map(activation => {
              const statusBadge = PLAYFUL_STATUS_BADGE[activation.status] || PLAYFUL_STATUS_BADGE.lobby;
              const finished = activation.status === 'finished';
              const activatedAt = activation.started_at || activation.created_at;
              const activatedDate = activatedAt ? new Date(activatedAt) : null;

              return (
                <TableRow key={activation.id}>
                  <TableCell>
                    {activatedDate ? (
                      <span
                        className="text-sm font-bold"
                        style={{ color: '#222731' }}
                      >
                        {activatedDate.toLocaleDateString('pt-BR')} <span className="text-sm font-normal text-neutral-400">{activatedDate.toLocaleTimeString('pt-BR')}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-neutral-light">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {activation.host ? (
                      <TableUser>
                        <AvatarWrapper
                          size="sm"
                          src={activation.host.image}
                          text={activation.host.name}
                          className="shrink-0"
                        />
                        <TableUserContent>
                          <TableUserTitle
                            className="w-full truncate text-sm"
                            title={activation.host.name}
                          >
                            {activation.host.name}
                          </TableUserTitle>
                        </TableUserContent>
                      </TableUser>
                    ) : (
                      <span className="text-sm text-neutral-light">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    <span className="text-sm text-neutral-dark">{activation.participants_count ?? 0}</span>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant="muted"
                      size="sm"
                      className="rounded-full font-mono text-sm"
                    >
                      {activation.access_code || '—'}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={statusBadge.variant}
                      size="sm"
                      className="rounded-full text-sm"
                    >
                      {statusBadge.label}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    <div className="flex justify-end">
                      {canOpenPanel(activation) && (
                        <PlayfulCardButton
                          variant={finished ? undefined : 'outline'}
                          className="h-7 w-auto px-4 text-sm"
                          onClick={() => navigate(PATHS.LIVE_CHALLENGE.ADMIN_PANEL(activation.id))}
                        >
                          {finished ? 'Ver resultado' : 'Abrir painel'}
                        </PlayfulCardButton>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <ul className="overflow-hidden rounded-xl bg-white shadow-dropshadow-sm [&>li:last-child]:border-b-0">
          {activations.map(activation => {
            const config = STATUS_CONFIG[activation.status] || STATUS_CONFIG.lobby;
            return (
              <li
                key={activation.id}
                className="flex flex-col gap-3 border-b border-neutral-lightest px-5 py-3 md:flex-row md:items-center md:gap-6"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="m-0 text-sm font-semibold text-neutral-dark">{formatDateTime(activation.started_at || activation.created_at)}</h3>
                    <Badge
                      variant={config.variant}
                      size="xs"
                      className="text-sm"
                    >
                      {config.label}
                    </Badge>
                  </div>
                  <p className="mt-0.5 text-sm text-neutral">
                    Host: {activation.host?.name || '—'} &middot; {activation.participants_count ?? 0} participantes &middot; Código: {activation.access_code || '—'}
                  </p>
                  {Array.isArray(activation.top_participants) && activation.top_participants.length > 0 && (
                    <p className="mt-0.5 text-xs text-neutral-light">
                      Top 3:{' '}
                      {activation.top_participants
                        .slice(0, 3)
                        .map((p, i) => `${i + 1}º ${p.name} (${p.total_score})`)
                        .join(' · ')}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  {canOpenPanel(activation) && (
                    <Button
                      type="button"
                      variant={activation.status === 'finished' ? 'success' : 'default'}
                      size="xs"
                      onClick={() => navigate(PATHS.LIVE_CHALLENGE.ADMIN_PANEL(activation.id))}
                    >
                      {activation.status === 'finished' ? 'Ver resultado' : 'Abrir painel'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardContent>
  );
}
